import { defineStore } from 'pinia'
import axios from 'axios'
import { BASE_URL } from '@/environment/environment'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import type { IFilterChoiceResultItem, IFilterChoicesResponse, IFilterMeta, IGraphResponse } from '@/modules/dashboard/types/graph/IGraphResponse'
import type { IGraphActiveFilter, IGraphStoreState } from '@/modules/dashboard/types/graph/IGraphStoreState'
import type { IGraphTarget } from '@/modules/dashboard/types/graph/IGraphTarget'
import type { ITableWidget } from '@/modules/dashboard/types/graph/ITableWidget'
import type { IUniversalTreeSelectModule } from '@/types/IUniversalTreeSelectModule'
import { onboardingApiUrl } from '@/modules/onboarding/utils/demoApi'
import type { TOnboardingSection } from '@/modules/onboarding/types/TOnboardingSection'

interface IGraphTargetEndpoints {
  /** Пути от `/api` */
  graph: string
  filterChoices: string

  /** Раздел онбординга, у которого на беке есть учебный двойник ручки графика */
  onboardingSection?: TOnboardingSection

  /** Учебный двойник filter-choices с тем же контрактом, что у боевой ручки */
  hasOnboardingChoices?: boolean
}

const TARGET_ENDPOINTS: Record<IGraphTarget, IGraphTargetEndpoints> = {
  audit: {
    graph: '/audit/graph/',
    filterChoices: '/audit/graph/filter-choices/',
    onboardingSection: 'audit',
    hasOnboardingChoices: true,
  },
  ticket: {
    graph: '/ticket/ticket/graph/',
    filterChoices: '/ticket/ticket/graph/filter-choices/',
    onboardingSection: 'ticket',
    hasOnboardingChoices: true,
  },
  adverse: {
    graph: '/adverse/adverse-event/graph/',
    filterChoices: '/adverse/adverse-event/graph/filter-choices/',
    onboardingSection: 'adverse_event',
    hasOnboardingChoices: true,
  },
  suggestion: {
    graph: '/improvements/suggestion/graph/',
    filterChoices: '/improvements/suggestion/graph/filter-choices/',
  },
  employee: {
    graph: '/employee/graph/',
    filterChoices: '/employee/graph/filter-choices/',
  },
}

function endpointUrl(path: string, onboardingSection?: TOnboardingSection): string {
  return onboardingSection ? onboardingApiUrl(onboardingSection, path) : `${BASE_URL}/api${path}`
}

export const useGraphStore = defineStore('graphStore', {
  state: (): IGraphStoreState => ({
    filters: [],
    widgets: [],
    isLoading: false,
    currentTarget: null,
    activeFilters: {},
    groupBy: null,
    dateTrunc: null,
    querySyncTab: null,

    // Реализация контракта IFilterStore для UniversalFilterTree
    filterChoices: {},
    searchChoices: {},
    filterCursors: {},
    filterLoading: {},
    filterStrategies: {},
    filterTotals: {},
    filterPages: {},
  }),

  getters: {
    // Есть ли хотя бы один активный фильтр — для условного показа кнопки сброса.
    hasActiveFilters(state): boolean {
      return Object.keys(state.activeFilters).length > 0
    },

    /*
     * Массив элементов для рендера чипов. По решению UX — текст чипа это только label
     * (имя выбранной строки), без префикса с названием виджета. Поддерживает как одиночные,
     * так и множественные фильтры.
     */
    activeFilterChips(state): Array<{ key: string; id: string | number; label: string; filterLabel?: string }> {
      const findLabelInChoices = (items: any[], id: string | number): string | null => {
        if (!items || !items.length) return null
        for (const it of items) {
          if (String(it.id) === String(id)) return String(it.label || it.name || it.title || it.id)
          if (it.children?.length) {
            const found = findLabelInChoices(it.children, id)
            if (found) return found
          }
          if (it.childrens?.length) {
            const found = findLabelInChoices(it.childrens, id)
            if (found) return found
          }
        }
        return null
      }

      const chips: Array<{ key: string; id: string | number; label: string; filterLabel?: string }> = []

      for (const [key, value] of Object.entries(state.activeFilters)) {
        const filterMeta = state.filters.find(f => f.key === key)
        const filterPrefix = filterMeta?.label ? `${filterMeta.label}: ` : ''
        const choices = state.filterChoices[key] || state.searchChoices[key] || []

        if (Array.isArray(value)) {
          value.forEach(item => {
            const valueLabel = item.label || findLabelInChoices(choices, item.id) || String(item.id)
            chips.push({
              key,
              id: item.id,
              label: `${filterPrefix}${valueLabel}`,
              filterLabel: filterMeta?.label,
            })
          })
        } else if (value && typeof value === 'object' && 'id' in value) {
          const item = value as IGraphActiveFilter
          const valueLabel = item.label || findLabelInChoices(choices, item.id) || String(item.id)
          chips.push({
            key,
            id: item.id,
            label: `${filterPrefix}${valueLabel}`,
            filterLabel: filterMeta?.label,
          })
        }
      }
      return chips
    },

    // Есть ли хотя бы один виджет графика line_chart на странице
    hasLineChartWidget(state): boolean {
      return state.widgets.some(w => w.widget_type === 'line_chart')
    },
  },

  actions: {
    /**
     * Формирует объект параметров из текущих активных фильтров для передачи в запросы.
     */
    getActiveFiltersParams(): Record<string, any> {
      const activeFiltersParams: Record<string, any> = {}
      for (const [key, value] of Object.entries(this.activeFilters)) {
        if (Array.isArray(value)) {
          if (value.length > 0) {
            activeFiltersParams[key] = value.map(item => item.id).join(',')
          }
        } else if (value && typeof value === 'object' && 'id' in value) {
          activeFiltersParams[key] = (value as IGraphActiveFilter).id
        }
      }
      return activeFiltersParams
    },

    /**
     * Загрузка графиков с бэкенда для выбранного target (audit, ticket, adverse, suggestion, employee)
     */
    async fetchGraph(target: IGraphTarget, filters: Record<string, any> = {}) {
      const feedbackStore = useFeedbackStore()
      this.isLoading = true
      feedbackStore.isGlobalLoading = true
      this.currentTarget = target

      try {
        const config = TARGET_ENDPOINTS[target]
        if (!config) {
          throw new Error(`Unknown target: ${target}`)
        }

        const params: Record<string, any> = {
          ...this.getActiveFiltersParams(),
          ...filters,
        }

        if (this.groupBy) {
          params.group_by = this.groupBy
        }

        if (this.dateTrunc) {
          params.date_trunc = this.dateTrunc
        }

        const res = await axios.get<IGraphResponse | any[]>(endpointUrl(config.graph, config.onboardingSection), {
          params,
        })

        if (Array.isArray(res.data)) {
          this.widgets = res.data
        } else {
          if (res.data.filters && res.data.filters.length > 0) {
            this.filters = res.data.filters
          }
          this.widgets = res.data.widgets || []
        }

        this.populateFilterLabels()
      } catch (error) {
        const message = getApiError(error, 'Ошибка загрузки графиков')
        feedbackStore.showToast({ type: 'error', message })
      } finally {
        this.isLoading = false
        feedbackStore.isGlobalLoading = false
      }
    },

    /**
     * Загрузка вариантов значений для конкретного фильтра (реализация контракта IFilterStore).
     * Прокидывает текущие активные фильтры для каскадной фильтрации choices.
     */
    async fetchFilterChoices(
      fieldKey: string,
      search = '',
      cursor?: string,
      _module?: IUniversalTreeSelectModule,
      target?: IGraphTarget,
    ) {
      if (!fieldKey || fieldKey === '__saved_presets__') return

      const activeTarget = target || this.currentTarget
      if (!activeTarget) return

      if (!this.currentTarget) {
        this.currentTarget = activeTarget
      }

      const config = TARGET_ENDPOINTS[activeTarget]
      if (!config?.filterChoices) return

      this.filterLoading[fieldKey] = true

      try {
        const params: Record<string, any> = {
          key: fieldKey,
          ...this.getActiveFiltersParams(),
        }

        if (search) {
          params.search_field = search
        }

        if (cursor) {
          if (!isNaN(Number(cursor))) {
            params.page = Number(cursor)
          } else {
            try {
              const url = new URL(cursor)
              const page = url.searchParams.get('page') || url.searchParams.get('cursor')
              if (page) params.page = page
            } catch {
              params.cursor = cursor
            }
          }
        }

        const res = await axios.get<IFilterChoicesResponse | IFilterChoiceResultItem[]>(
          endpointUrl(config.filterChoices, config.hasOnboardingChoices ? config.onboardingSection : undefined),
          { params },
        )

        let results: IFilterChoiceResultItem[] = []
        let nextCursor: string | null = null
        let totalCount = 0

        if (Array.isArray(res.data)) {
          results = res.data
          totalCount = results.length
        } else {
          results = res.data.results || []
          totalCount = res.data.count ?? results.length
          nextCursor = res.data.next || null
        }

        if (search) {
          this.searchChoices[fieldKey] = results
        } else {
          if (cursor) {
            const current = this.filterChoices[fieldKey] || []
            this.filterChoices[fieldKey] = [...current, ...results]
          } else {
            this.filterChoices[fieldKey] = results
          }
          this.filterCursors[fieldKey] = nextCursor
          this.filterTotals[fieldKey] = totalCount
        }
      } catch (error) {
        const feedbackStore = useFeedbackStore()
        const message = getApiError(error, `Ошибка при загрузке вариантов фильтра [${fieldKey}]`)
        feedbackStore.showToast({ type: 'error', message })
        console.error(message, error)
      } finally {
        this.filterLoading[fieldKey] = false
      }
    },

    /**
     * Перезапрашивает choices для всех доступных фильтров с учетом текущих activeFilters.
     */
    async refetchAllFilterChoices(target?: IGraphTarget) {
      const activeTarget = target || this.currentTarget
      if (!activeTarget || !this.filters.length) return

      await Promise.all(
        this.filters.map(f => this.fetchFilterChoices(f.key, '', undefined, undefined, activeTarget)),
      )
    },

    /**
     * Переключение фильтра по клику на элемент.
     */
    async toggleGraphFilter(
      target: IGraphTarget,
      key: string,
      id: string | number,
      label: string,
    ) {
      const current = this.activeFilters[key]
      if (Array.isArray(current)) {
        const exists = current.some(item => String(item.id) === String(id))
        if (exists) {
          const nextList = current.filter(item => String(item.id) !== String(id))
          if (nextList.length > 0) {
            this.activeFilters[key] = nextList
          } else {
            const next = { ...this.activeFilters }
            delete next[key]
            this.activeFilters = next
          }
        } else {
          this.activeFilters[key] = [...current, { id, label }]
        }
      } else if (current && !Array.isArray(current)) {
        if (String(current.id) === String(id)) {
          const next = { ...this.activeFilters }
          delete next[key]
          this.activeFilters = next
        } else {
          this.activeFilters[key] = [current, { id, label }]
        }
      } else {
        this.activeFilters[key] = [{ id, label }]
      }

      await Promise.all([
        this.fetchGraph(target),
        this.refetchAllFilterChoices(target),
      ])
    },

    /**
     * Снятие одного фильтра по клику на крестик чипа.
     */
    async removeGraphFilter(target: IGraphTarget, key: string, id?: string | number) {
      if (!(key in this.activeFilters)) return

      const current = this.activeFilters[key]
      if (Array.isArray(current)) {
        if (id !== undefined) {
          const nextList = current.filter(item => String(item.id) !== String(id))
          if (nextList.length > 0) {
            this.activeFilters[key] = nextList
          } else {
            const next = { ...this.activeFilters }
            delete next[key]
            this.activeFilters = next
          }
        } else {
          const next = { ...this.activeFilters }
          delete next[key]
          this.activeFilters = next
        }
      } else {
        const next = { ...this.activeFilters }
        delete next[key]
        this.activeFilters = next
      }

      if (this.groupBy === key) {
        const remaining = this.activeFilters[key]
        if (!remaining || (Array.isArray(remaining) && remaining.length < 2)) {
          this.groupBy = null
        }
      }

      await Promise.all([
        this.fetchGraph(target),
        this.refetchAllFilterChoices(target),
      ])
    },

    /**
     * Сброс всех активных фильтров.
     */
    async resetGraphFilters(target?: IGraphTarget) {
      this.activeFilters = {}
      this.groupBy = null
      if (target) {
        await Promise.all([
          this.fetchGraph(target),
          this.refetchAllFilterChoices(target),
        ])
      }
    },

    /**
     * Установка массива выбранных значений фильтра для указанного ключа.
     */
    async setGraphFilterValues(
      target: IGraphTarget,
      key: string,
      selected: Array<{ id: string | number; label: string }>,
    ) {
      if (selected.length > 0) {
        this.activeFilters[key] = selected
      } else {
        const next = { ...this.activeFilters }
        delete next[key]
        this.activeFilters = next
      }
      await Promise.all([
        this.fetchGraph(target),
        this.refetchAllFilterChoices(target),
      ])
    },

    /**
     * Установка выбранных ID из UniversalFilterTree (массив string)
     */
    async updateFilterTreeSelection(
      target: IGraphTarget,
      key: string,
      selectedIds: string[],
    ) {
      if (!selectedIds || selectedIds.length === 0) {
        const next = { ...this.activeFilters }
        delete next[key]
        this.activeFilters = next
      } else {
        // Поиск названий в filterChoices / searchChoices
        const choices = [
          ...(this.filterChoices[key] || []),
          ...(this.searchChoices[key] || []),
        ]

        const findLabel = (nodes: IFilterChoiceResultItem[], id: string): string | undefined => {
          for (const node of nodes) {
            if (String(node.id) === id) return node.label
            if (node.children?.length) {
              const f = findLabel(node.children, id)
              if (f) return f
            }
          }
          return undefined
        }

        const items = selectedIds.map(id => {
          const existingLabel = Array.isArray(this.activeFilters[key])
            ? (this.activeFilters[key] as any[]).find(item => String(item.id) === String(id))?.label
            : undefined
          const label = existingLabel || findLabel(choices, id) || id
          return { id, label }
        })

        this.activeFilters[key] = items
      }

      await Promise.all([
        this.fetchGraph(target),
        this.refetchAllFilterChoices(target),
      ])
    },

    // Восстанавливает человеческие названия (label) для активных фильтров
    populateFilterLabels() {
      // 1. Из таблиц
      for (const widget of this.widgets) {
        if (widget.widget_type !== 'table') continue

        const tableWidget = widget as ITableWidget
        if (!tableWidget.data || !tableWidget.data.key) continue

        const key = tableWidget.data.key
        const current = this.activeFilters[key]
        if (!current) continue

        const labels = tableWidget.data.labels || []
        const findLabel = (id: any) => {
          const found = labels.find((l: any) => String(l.id) === String(id))
          return found ? found.name : String(id)
        }

        if (Array.isArray(current)) {
          this.activeFilters[key] = current.map(item => ({
            id: item.id,
            label: item.label || findLabel(item.id),
          }))
        } else if (current) {
          this.activeFilters[key] = {
            id: current.id,
            label: current.label || findLabel(current.id),
          }
        }
      }

      // 2. Из загруженных filterChoices
      for (const [key, choices] of Object.entries(this.filterChoices)) {
        const current = this.activeFilters[key]
        if (!current) continue

        const findLabel = (nodes: IFilterChoiceResultItem[], id: any): string | undefined => {
          for (const node of nodes) {
            if (String(node.id) === String(id)) return node.label
            if (node.children?.length) {
              const f = findLabel(node.children, id)
              if (f) return f
            }
          }
          return undefined
        }

        if (Array.isArray(current)) {
          this.activeFilters[key] = current.map(item => ({
            id: item.id,
            label: (item.label && item.label !== String(item.id)) ? item.label : (findLabel(choices, item.id) || item.label || String(item.id)),
          }))
        } else if (current) {
          this.activeFilters[key] = {
            id: current.id,
            label: (current.label && current.label !== String(current.id)) ? current.label : (findLabel(choices, current.id) || current.label || String(current.id)),
          }
        }
      }
    },

    /**
     * Установка режима группировки (вывода показателя на общем графике).
     */
    async setGroupBy(target: IGraphTarget, groupBy: string | null) {
      this.groupBy = groupBy
      await this.fetchGraph(target)
    },

    /**
     * Установка режима детализации времени на графике (date_trunc).
     */
    async setDateTrunc(target: IGraphTarget, dateTrunc: string | null) {
      this.dateTrunc = dateTrunc
      await this.fetchGraph(target)
    },

    /**
     * Полный сброс состояния хранилища графиков.
     */
    reset() {
      this.filters = []
      this.widgets = []
      this.isLoading = false
      this.currentTarget = null
      this.activeFilters = {}
      this.groupBy = null
      this.dateTrunc = null

      this.filterChoices = {}
      this.searchChoices = {}
      this.filterCursors = {}
      this.filterLoading = {}
      this.filterStrategies = {}
      this.filterTotals = {}
      this.filterPages = {}
    },
  },
})

